import { Inject, Injectable } from '@angular/core';
import { mapFetchResultData } from '@x/common/graph';
import { Observable } from 'rxjs';
import {
  ShopCancelCartPaymentGQL,
  ShopCheckVoucherPaymentBalanceGQL,
  ShopCreateCartPaymentGQL,
  ShopCreateNewCartPaymentGQL,
  ShopPaymentGQL,
  ShopPaymentMethodsGQL,
  ShopReconCheckoutPaymentGQL,
  ShopRedeemCartPaymentGQL,
} from './gql/shop-payment.gql.generated';
import { AUTHORIZATION_HEADER_PROVIDER, AuthorizationHeaderProvider } from './types/shop-auth';
import {
  IShopCancelCartPaymentInput,
  IShopCreateCartPaymentInput,
  IShopCreateNewCartPaymentInput,
  IShopPayment,
  IShopPaymentMethod,
  IShopPaymentMethodsQuery,
  IShopPaymentQuery,
  IShopReconCheckoutPaymentQuery,
  IShopRedeemCartPaymentInput,
  IShopVoucherPaymentBalance,
} from './types/shop-payment';

@Injectable()
export class ShopPaymentService {
  constructor(
    private shopCreateNewCartPaymentGQL: ShopCreateNewCartPaymentGQL,
    private shopCreateCartPaymentGQL: ShopCreateCartPaymentGQL,
    private shopPaymentMethodsGQL: ShopPaymentMethodsGQL,
    private shopReconCheckoutPaymentGQL: ShopReconCheckoutPaymentGQL,
    private shopPaymentGQL: ShopPaymentGQL,
    private shopCheckVoucherPaymentBalanceGQL: ShopCheckVoucherPaymentBalanceGQL,
    private shopRedeemCartPaymentGQL: ShopRedeemCartPaymentGQL,
    private shopCancelCartPaymentGQL: ShopCancelCartPaymentGQL,
    @Inject(AUTHORIZATION_HEADER_PROVIDER)
    private authHeader: AuthorizationHeaderProvider,
  ) {
    if (!authHeader) throw new Error('authHeader Provider is required');
  }

  fetchPaymentMethods(query: IShopPaymentMethodsQuery): Observable<IShopPaymentMethod[]> {
    return this.shopPaymentMethodsGQL
      .fetch(query)
      .pipe(mapFetchResultData((r) => r.shopPaymentMethods));
  }

  createNewCartPayment(query: IShopCreateNewCartPaymentInput): Observable<IShopPayment> {
    return this.shopCreateNewCartPaymentGQL
      .mutate({ input: query }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCreateNewCartPayment));
  }

  createCartPayment(query: IShopCreateCartPaymentInput): Observable<IShopPayment> {
    return this.shopCreateCartPaymentGQL
      .mutate({ input: query }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCreateCartPayment));
  }

  fetchShopPayment(input: IShopPaymentQuery): Observable<IShopPayment> {
    return this.shopPaymentGQL
      .fetch(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopPayment));
  }

  reconCheckoutPayment(input: IShopReconCheckoutPaymentQuery): Observable<IShopPayment> {
    return this.shopReconCheckoutPaymentGQL
      .fetch(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopReconCheckoutPayment));
  }

  checkVoucherPaymentBalance(
    input: IShopRedeemCartPaymentInput,
  ): Observable<IShopVoucherPaymentBalance> {
    return this.shopCheckVoucherPaymentBalanceGQL
      .fetch({ input }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCheckVoucherPaymentBalance));
  }

  redeemCartPayment(input: IShopRedeemCartPaymentInput): Observable<IShopPayment> {
    return this.shopRedeemCartPaymentGQL
      .mutate({ input }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopRedeemCartPayment));
  }

  cancelCartPayment(input: IShopCancelCartPaymentInput): Observable<IShopPayment> {
    return this.shopCancelCartPaymentGQL
      .mutate({ input }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCancelCartPayment));
  }
}
